import {
  MeasurementSystem,
  PicklistEventType,
  PointAndPlaneFragment,
} from '@warebee/frontend/data-access-api-graphql';
import Konva from 'konva';
import { LineConfig } from 'konva/lib/shapes/Line';
import _ from 'lodash';
import React, { useEffect, useRef, useState } from 'react';
import { Line } from 'react-konva';
import { useRecoilValue } from 'recoil';
import { TwTheme } from '../../../Tw';
import { warehouseMeasurementSystem } from '../../store/warehouse.state';
import { getSideOffset, RouteSide } from './route/sideOffset';

const routeColors = TwTheme.extend.colors.route;

// Animated Line Component (similar to AnimatedArrow but for lines)
type AnimatedLineProps = {
  points: number[];
  strokeWidth?: number;
  scale?: number;
  direction?: 'FORWARD' | 'BACKWARD';
  stroke?: string;
  opacity?: number;
  speed?: number;
  issue?: string | 'CONGESTION' | 'OUT_OF_SEQUENCE' | 'OUT_OF_STOCK' | 'OTHER';
};

const AnimatedLine: React.FC<AnimatedLineProps> = props => {
  const ref = useRef(null);
  const measureSystem = useRecoilValue(warehouseMeasurementSystem);
  const scale =
    props.scale ?? (measureSystem === MeasurementSystem.IMPERIAL ? 0.7 : 1);
  const dashSize = 4 * 3.5 * scale;
  const dashSpace = dashSize * 4;
  const fps = props.speed ? props.speed : 120 * scale;

  useEffect(() => {
    const anim = new Konva.Animation(frame => {
      if (!ref.current) return;
      const direction = props.direction === 'BACKWARD' ? 1 : -1;
      ref.current.dashOffset(
        (direction * ((frame.time / 1000) * fps)) %
          (dashSpace * (dashSize + dashSpace)),
      );
    }, ref.current?.getLayer());

    anim.start();
    return () => {
      anim.stop();
    };
  }, [props.direction, dashSize, dashSpace, fps]);

  const animatedLineConfig: LineConfig = {
    points: props.points,
    stroke: props.stroke || routeColors.selectedBorderDash,
    strokeWidth: props.strokeWidth || 12,
    closed: false,
    dashEnabled: true,
    dash: [dashSize, dashSpace],
    opacity: props.opacity || 0.8,
    lineCap: 'round',
    lineJoin: 'round',
    listening: false,
    perfectDrawEnabled: false,
  };

  const staticLineConfig: LineConfig = {
    points: props.points,
    stroke: props.stroke || routeColors.selectedBorder,
    strokeWidth: props.strokeWidth || 12,
    closed: false,
    opacity: (props.opacity || 0.8) * 0.5,
    lineCap: 'round',
    lineJoin: 'round',
    listening: false,
    perfectDrawEnabled: false,
  };

  return (
    <>
      <Line {...staticLineConfig} />
      <Line ref={ref} {...animatedLineConfig} />
    </>
  );
};

export type RouteFeatureLightProps = {
  points: PointAndPlaneFragment[];
  scale?: number;
  colorRoute?: string;
  colorRouteBackground?: string;
  animate?: boolean;
  direction?: 'FORWARD' | 'BACKWARD';
  eventType?: PicklistEventType;
  eventSpeed?: number;
  side?: 'LEFT' | 'RIGHT';
  isActive?: boolean;
};

const RouteFeatureLight: React.FC<RouteFeatureLightProps> = props => {
  const { points } = props;

  const [hoveredId, setHoveredId] = useState<string>(null);

  const scale = props.scale ?? 1;

  // Remove getXOffset, use getSideOffset instead

  const linePoints = _(points)
    .compact()
    .map(p => [p.x + getSideOffset(props.side as RouteSide, false) - 10, p.y])
    .flatten()
    .value();

  const backgroundPoints = _(points)
    .compact()
    .map(p => [p.x + getSideOffset(props.side as RouteSide, true) - 5, p.y])
    .flatten()
    .value();

  const lineRouteConfig: LineConfig = {
    points: linePoints,
    stroke: props.colorRoute ?? routeColors.line,
    strokeWidth: 12,
    closed: false,
    dash: [24, 48],
    opacity: 0.5,
    lineCap: 'round',
    lineJoin: 'round',
    globalCompositeOperation: 'multiply',
  };

  const linePathBackground: LineConfig = {
    points: backgroundPoints,
    stroke: props.colorRouteBackground ?? routeColors.DEFAULT,
    strokeWidth: 70,
    opacity: 0.5,
    lineCap: 'round',
    lineJoin: 'round',
    // globalCompositeOperation: 'multiply',
  };

  // Debug: Get first point for label positioning
  const firstPoint = points[0];
  const debugOffset = getSideOffset(props.side as RouteSide, false);

  // Position text differently for LEFT vs RIGHT to avoid overlap
  const textY = props.side === 'LEFT' ? firstPoint?.y - 50 : firstPoint?.y + 30;
  const textColor = props.side === 'LEFT' ? 'blue' : 'red';

  return (
    <>
      <Line {...linePathBackground} />

      {/* Debug Label */}
      {/* {firstPoint && (
        <Text
          x={firstPoint.x + debugOffset - 50}
          y={textY}
          text={`Side: ${props.side || 'NONE'} |  ${props.eventType}`}
          fontSize={14}
          fill={textColor}
          fontFamily="Arial"
          fontStyle="bold"
          scaleY={-1}
        />
      )} */}

      {props.animate ? (
        <AnimatedLine
          points={linePoints}
          strokeWidth={12}
          scale={scale}
          direction={props.direction}
          stroke={props.colorRoute ?? routeColors.line}
          opacity={0.5}
        />
      ) : (
        <Line {...lineRouteConfig} />
      )}
    </>
  );
};

export default React.memo(RouteFeatureLight);
